import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { functions } from "src/api/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { CheckCircle, Loader2, ArrowLeft } from "lucide-react";
import { EnhancedPlan } from "../SubscriptionPage/types";
import { Button } from "../../components/ui/Button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/ui/Card";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { usePayPalPlans } from "src/hooks/useFetchPaypalPlan";
import CheckoutSuccess from "./CheckoutSuccess";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import AccessCodeInput from "../../components/AccessCodeInput";

const CheckoutPage: React.FC = () => {
    const location = useLocation();
    const navigate = usePartnerAwareNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<EnhancedPlan | null>(null);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    
    // Get planId and access code from URL query parameter
    const query = new URLSearchParams(location.search);
    const planId = query.get("planId");
    const urlAccessCode = query.get("accessCode");
    const urlDiscount = query.get("discount");
    
    // Access code state - initialize from URL if provided
    const [accessCode, setAccessCode] = useState<string>(urlAccessCode || '');
    const [discountAmount, setDiscountAmount] = useState<number>(urlDiscount ? parseFloat(urlDiscount) : 0);

    // Reference to track if we just completed a subscription
    const justSubscribed = useRef(false);

    const { firebaseUser, subscription, checkSubscription, subscriptionPlan } =
        useAuth();

    // PayPal configuration
    const paypalOptions = {
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || "test",
        currency: "USD",
        intent: "subscription",
        components: "buttons",
        vault: true,
    };

    // Fetch plans
    const {
        data: plans = [],
        isLoading: plansLoading,
        isError,
    } = usePayPalPlans({
        isAuthenticated: Boolean(firebaseUser),
    });

    // Set selected plan based on planId from URL
    useEffect(() => {
        if (!plansLoading && plans.length > 0 && planId) {
            const plan = plans.find((p) => p.id === planId);
            if (plan) {
                setSelectedPlan(plan);
            } else {
                setError("Selected plan not found");
            }
            setLoading(false);
        } else if (!plansLoading && plans.length > 0) {
            setLoading(false);
        }
    }, [planId, plans, plansLoading]);

    // Redirect back to subscription page if user is already subscribed to this plan
    // BUT only if we haven't just completed a subscription
    useEffect(() => {
        if (
            !justSubscribed.current &&
            subscriptionPlan &&
            selectedPlan &&
            subscriptionPlan.id === selectedPlan.id &&
            subscription?.status === "ACTIVE" // Only redirect if subscription is active
        ) {
            navigate("/subscription");
        }
    }, [subscriptionPlan, selectedPlan, navigate, subscription?.status]);

    // Redirect if no planId is provided
    useEffect(() => {
        if (!planId && !loading) {
            navigate("/subscription");
        }
    }, [planId, loading, navigate]);

    // Handle successful subscription
    const handleSubscriptionApproved = async (data: any) => {
        if (!selectedPlan || !firebaseUser) return;
        console.log("=== Subscription Approved ===");
        console.log("Subscription ID:", data.subscriptionID);
        console.log("Plan:", selectedPlan.name);
        console.log("Discount Applied:", discountAmount);
        setProcessingPayment(true);
        setError(null);

        try {
            // Set flag to prevent redirect in the useEffect
            justSubscribed.current = true;

            // Create callable function
            const recordSubscriptionFunction = httpsCallable(
                functions,
                "recordSubscription"
            );

            // Call the function to record subscription with access code if used
            await recordSubscriptionFunction({
                subscriptionId: data.subscriptionID,
                planId: selectedPlan.id,
                userId: firebaseUser.uid,
                accessCode: accessCode || undefined,
                discountAmount: discountAmount || undefined,
            });

            // Refresh subscription status
            await checkSubscription();

            // Show success animation
            setProcessingPayment(false);
            setShowSuccessScreen(true);
        } catch (err) {
            console.error("Error recording subscription:", err);
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to complete your subscription. Please try again."
            );
            setProcessingPayment(false);
        }
    };

    // Handle go back
    const handleGoBack = () => {
        navigate("/subscription");
    };
    
    // Handle access code validation
    const handleValidAccessCode = (code: string, discount: number) => {
        console.log("=== Access Code Applied ===");
        console.log("Code:", code);
        console.log("Discount:", discount);
        setAccessCode(code);
        setDiscountAmount(discount);
        setError(null);
    };
    
    const handleInvalidAccessCode = () => {
        console.log("=== Access Code Removed ===");
        setAccessCode('');
        setDiscountAmount(0);
    };

    if (loading || plansLoading) {
        return (
            <div className="container mx-auto px-4 py-12 max-w-2xl">
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
                </div>
            </div>
        );
    }

    if (!selectedPlan) {
        return (
            <div className="container mx-auto px-4 py-12 max-w-2xl">
                <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                    <AlertDescription>
                        Plan not found. Please go back and select a valid plan.
                    </AlertDescription>
                </Alert>
                <Button
                    variant="outline"
                    className="mt-4 text-gray-700 border-gray-300 hover:bg-gray-100 transition-all duration-300"
                    onClick={handleGoBack}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Plans
                </Button>
            </div>
        );
    }

    // Determine colors based on plan type
    const getThemeColor = () => {
        if (selectedPlan.name.toLowerCase().includes("plus")) {
            return "bg-gradient-to-br from-orange-400 to-orange-500";
        } else if (selectedPlan.name.toLowerCase().includes("pro")) {
            return "bg-gradient-to-br from-purple-400 to-purple-500";
        } else {
            return "bg-gradient-to-br from-blue-400 to-blue-500";
        }
    };

    return (
        <div className="container mx-auto px-4 py-12 max-w-2xl">
            {/* Success Screen */}
            {showSuccessScreen && selectedPlan && (
                <CheckoutSuccess
                    plan={selectedPlan}
                    onDone={() => {
                        // Navigate to dashboard after animation
                        navigate("/");
                    }}
                />
            )}

            {/* Header with back button */}
            <div className="flex items-center mb-8">
                <Button
                    variant="ghost"
                    onClick={handleGoBack}
                    className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-300">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Plans
                </Button>
            </div>

            <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">
                Subscribe to {selectedPlan.name} Plan
            </h1>

            <Card className="border border-gray-200 bg-white shadow-lg mb-6 transition-all duration-500 hover:shadow-xl transform hover:scale-[1.01]">
                <CardHeader className={`${getThemeColor()} rounded-t-lg`}>
                    <CardTitle className="text-xl font-bold text-white">
                        {selectedPlan.name} Plan
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 pb-6">
                    {/* Access Code Input - Only show if not provided from URL */}
                    {!urlAccessCode && (
                        <AccessCodeInput
                            onValidCode={handleValidAccessCode}
                            onInvalidCode={handleInvalidAccessCode}
                            disabled={processingPayment}
                            className="mb-6"
                        />
                    )}
                    
                    <div className="flex justify-between mb-6">
                        <div>
                            <p className="text-gray-600">Price</p>
                            <div className="flex items-baseline">
                                {discountAmount > 0 ? (
                                    <>
                                        <span className="text-2xl font-bold text-gray-800">
                                            ${Math.max(0, selectedPlan.price - discountAmount)}
                                        </span>
                                        <span className="text-lg text-gray-500 line-through ml-2">
                                            ${selectedPlan.price}
                                        </span>
                                    </>
                                ) : (
                                    <span className="text-2xl font-bold text-gray-800">
                                        ${selectedPlan.price}
                                    </span>
                                )}
                                {selectedPlan.interval && (
                                    <span className="text-sm ml-1 text-gray-500">
                                        / {selectedPlan.interval}
                                    </span>
                                )}
                            </div>
                            {discountAmount > 0 && (
                                <p className="text-sm text-green-600 mt-1">
                                    {urlAccessCode ? (
                                        <>Access code "{accessCode}" applied - You save ${discountAmount.toFixed(2)} on your first month</>
                                    ) : (
                                        <>You save ${discountAmount.toFixed(2)} on your first month with access code</>
                                    )}
                                </p>
                            )}
                        </div>
                        {selectedPlan.trialPeriod && (
                            <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg flex items-center">
                                <span className="text-sm">
                                    Includes{" "}
                                    {selectedPlan.trialPeriod.formatted} free
                                    trial
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="mb-6 pb-6 border-b border-gray-200">
                        <p className="text-sm text-gray-600 mb-3">
                            Plan features:
                        </p>
                        <ul className="space-y-3">
                            {selectedPlan.features.map((feature, idx) => (
                                <li
                                    key={idx}
                                    className="flex items-start gap-2 text-gray-700">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {error && (
                        <Alert className="mb-6 bg-red-50 border-red-200 text-red-700">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {/* Payment section */}
                    <div className="mt-4">
                        {processingPayment ? (
                            <div className="flex justify-center items-center h-16 bg-gray-100 rounded-lg">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-600 mr-2" />
                                <span className="text-gray-700">
                                    Processing your subscription...
                                </span>
                            </div>
                        ) : selectedPlan.price > 0 ? (
                            <div className="w-full bg-gray-50 p-4 rounded-lg border border-gray-200 transition-all duration-300 hover:border-gray-300">
                                <PayPalScriptProvider options={paypalOptions}>
                                    <PayPalButtons
                                        style={{
                                            layout: "vertical",
                                            color: "gold",
                                            shape: "rect",
                                            label: "subscribe",
                                            height: 50,
                                        }}
                                        createSubscription={(data, actions) => {
                                            console.log("=== PayPal Subscription Creation Started ===");
                                            
                                            // Determine which plan ID to use based on access code
                                            let planIdToUse = selectedPlan.id;
                                            
                                            if (accessCode && discountAmount > 0) {
                                                // Look for a discounted plan by name
                                                // Expected name format: "Original Plan Name - ACCESS_CODE"
                                                // Access code is case-sensitive
                                                const discountedPlanName = `${selectedPlan.name} - ${accessCode}`;
                                                
                                                console.log("Looking for discounted plan with name:", discountedPlanName);
                                                console.log("Access Code:", accessCode);
                                                console.log("Discount Amount:", discountAmount);
                                                
                                                // Check if a plan with this name exists (case-sensitive)
                                                const discountedPlan = plans.find((p: any) => 
                                                    p.name === discountedPlanName
                                                );
                                                
                                                if (discountedPlan) {
                                                    planIdToUse = discountedPlan.id;
                                                    console.log("Found discounted plan!");
                                                    console.log("- Name:", discountedPlan.name);
                                                    console.log("- ID:", discountedPlan.id);
                                                    console.log("- Price:", discountedPlan.price);
                                                } else {
                                                    console.warn(`Discounted plan with name "${discountedPlanName}" not found. Using regular plan.`);
                                                    console.log("Available plans:", plans.map((p: any) => ({
                                                        name: p.name,
                                                        id: p.id,
                                                        price: p.price
                                                    })));
                                                }
                                            }
                                            
                                            const subscriptionRequest: any = {
                                                plan_id: planIdToUse,
                                                custom_id: accessCode || undefined, // Track which access code was used
                                            };
                                            
                                            console.log("Final subscription request:", subscriptionRequest);
                                            
                                            return actions.subscription.create(subscriptionRequest)
                                                .then((orderId: string) => {
                                                    console.log("PayPal subscription created successfully:", orderId);
                                                    return orderId;
                                                })
                                                .catch((error: any) => {
                                                    console.error("PayPal subscription creation error:", error);
                                                    throw error;
                                                });
                                        }}
                                        onApprove={(data, actions) => {
                                            handleSubscriptionApproved(data);
                                            return Promise.resolve();
                                        }}
                                        onError={(err) => {
                                            console.error("PayPal error:", err);
                                            setError(
                                                "There was a problem with the payment process. Please try again."
                                            );
                                        }}
                                        onCancel={() => {
                                            console.log(
                                                "Subscription canceled"
                                            );
                                        }}
                                    />
                                </PayPalScriptProvider>
                            </div>
                        ) : (
                            <Button
                                className="w-full py-6 text-base font-medium bg-green-500 hover:bg-green-600 text-white transition-all duration-300 transform hover:scale-[1.02]"
                                onClick={() => {
                                    // For free plans, show success screen too
                                    justSubscribed.current = true;
                                    setShowSuccessScreen(true);
                                }}>
                                Activate Free Plan
                            </Button>
                        )}
                    </div>

                    <p className="text-xs text-gray-500 mt-4 text-center">
                        By subscribing, you agree to our Terms of Service and
                        Privacy Policy. You can cancel your subscription at any
                        time.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default CheckoutPage;
